import {
    EditIcon,
    ExternalLinkIcon,
    InfoOutlineIcon,
    RepeatIcon,
} from '@chakra-ui/icons';
import { TbTrash } from 'react-icons/tb';
import {
    IconButton,
    Tooltip,
    forwardRef,
    Progress,
    HStack,
    Text,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styles from '../../../styles';

import { MenuItem as ChakraMenuItem } from '@/components/v4';
import { Card, CardBody } from 'components/v4/Card';
import DashboardCardEditButton from '../../DashboardCardEditButton';
import ServiceCardLayout, { ServiceCardDetail } from '../../ServiceCardLayout';
import updateVendorDefinedStatus from 'API/Service/updateVendorDefinedStatus';
import { getStatusConfig } from './helper';
import { serviceReducer } from './serviceReducer';
import ServiceStatus from '../../ServiceStatus';

import vendor from 'assets/images/vendor.png';
import { ACTIONS } from './constant';
import { axios } from '@/services/axios';
import { toast } from 'react-toastify';
import { PlatformEnums } from 'utils/platforms';
const MenuItem = forwardRef((props, ref) => {
    const { darkMode } = useSelector((state) => state.settings);

    return (
        <ChakraMenuItem
            sx={{
                '& svg': {
                    color: darkMode ? 'white' : 'black',
                },
            }}
            ref={ref}
            {...props}
        />
    );
});
const VendorCard = ({ service }) => {
    const { darkMode } = useSelector((state) => state.settings);
    const [state, dispatch] = useReducer(serviceReducer, service);
    const cardStatus = getStatusConfig(state.statusDisplay);

    // (893/1223) The backend does not supply a per-service number count yet.
    // Render the row only once a real count arrives — never fall back to a
    // placeholder, because a wrong count is worse than no count.
    const activeNumbers = service?.activeNumbers;
    const showActiveNumbers =
        service?.platformID === PlatformEnums.OPERATOR_PLATFORM &&
        typeof activeNumbers === 'number';

    // Keep local reducer state in sync with consolidated dashboard payload updates.
    useEffect(() => {
        if (!service) return;
        dispatch({
            type: ACTIONS.UPDATE_SERVICE,
            payload: service,
        });
        if (service?.actions) {
            dispatch({
                type: ACTIONS.UPDATE_SERVICE_ACTIONS,
                payload: service.actions,
            });
        }
    }, [service]);

    const updateStatus = useMutation({
        mutationFn: (serviceStatusID) =>
            updateVendorDefinedStatus(state.id, serviceStatusID),
        onSuccess: (newService) => {
            dispatch({
                type: ACTIONS.UPDATE_SERVICE,
                payload: newService,
            });
            if (newService?.actions?.length > 0)
                dispatch({
                    type: ACTIONS.UPDATE_SERVICE_ACTIONS,
                    payload: newService.actions,
                });
        },
    });

    const handleClick = (action) => {
        if (action.serviceStatusID) {
            return updateStatus.mutate(action.serviceStatusID);
        } else if (action.externalURL) {
            window.open(action.externalURL, '_blank');
        }
    };

    const deleteService = async () => {
        try {
            await axios.delete(`/service/vendordefined/${service?.id}`);
            toast.success('Vendor defined service removed successfully!');
        } catch (e) {
            toast.error(e.response.data.message);
        }
    };

    return (
        <Card
            bg={darkMode ? 'dark.tcap' : null}
            sx={{
                ...styles.serviceCard,
                border: '1px solid',
                borderColor: darkMode ? 'gray.700' : 'gray.200',
                boxShadow: darkMode
                    ? 'none'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            }}>
            <Progress
                value={100}
                sx={styles.verticalBar}
                colorScheme={cardStatus?.color}
                hasStripe={cardStatus?.isAnimated}
                isAnimated={cardStatus?.isAnimated}
            />
            <CardBody
                sx={{
                    display: 'flex',
                    flexDir: 'column',
                    ...styles.serviceCardBody,
                }}>
                <ServiceCardLayout
                    icon={
                        isEmpty(service.displayImage)
                            ? vendor
                            : // eslint-disable-next-line no-undef
                              process.env.NODE_ENV === 'development'
                              ? import.meta.env.VITE_APP_IMAGE_BASE +
                                '/images/' +
                                service.displayImage
                              : window.location.origin +
                                '/images/' +
                                service.displayImage
                    }
                    name={service.name}
                    status={
                        <HStack spacing={1}>
                            <ServiceStatus
                                service={service}
                                cardStatus={cardStatus}
                                isVendorCard={true}
                            />
                            {state.statusDisplay?.statusDisplayExtended && (
                                <Tooltip
                                    label={
                                        state.statusDisplay
                                            ?.statusDisplayExtended
                                    }
                                    hasArrow
                                    placement="bottom">
                                    <IconButton
                                        icon={
                                            <InfoOutlineIcon
                                                color={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        size="xs"
                                        bg="transparent"
                                        _hover={{
                                            bg: 'transparent',
                                            boxShadow: 'none',
                                        }}
                                        _active={{
                                            bg: 'transparent',
                                            boxShadow: 'none',
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </HStack>
                    }>
                    {/* Active Numbers */}
                    {showActiveNumbers && (
                        <ServiceCardDetail label="Active Numbers:">
                            {activeNumbers}
                        </ServiceCardDetail>
                    )}
                </ServiceCardLayout>
                <DashboardCardEditButton>
                    {state?.allowEdit && (
                        <Link
                            id="edit-service"
                            className="bg-transparent !no-underline hover:text-inherit"
                            to={`/services/vendordefined/${service.id}/edit`}
                            state={service}>
                            <MenuItem icon={<EditIcon />}>
                                <Text marginBottom="0">Edit Service</Text>
                            </MenuItem>
                        </Link>
                    )}
                    {state?.allowUserEdit && (
                        <Link
                            id="activate-users"
                            className="bg-transparent !no-underline hover:text-inherit"
                            to={`/services/vendordefined/${service.id}/users`}
                            state={service}>
                            <MenuItem icon={<EditIcon />}>
                                <Text marginBottom="0">Activate Users</Text>
                            </MenuItem>
                        </Link>
                    )}
                    {state?.allowNumberEdit && (
                        <Link
                            id="activate-numbers"
                            className="bg-transparent !no-underline hover:text-inherit"
                            to={`/services/vendordefined/${service.id}/numbers`}
                            state={service}>
                            <MenuItem icon={<EditIcon />}>
                                <Text marginBottom="0">Activate Numbers</Text>
                            </MenuItem>
                        </Link>
                    )}
                    {state?.actions?.length > 0 &&
                        state?.actions?.map((action, index) => {
                            // TODO: need switch case to dynamically render the actions
                            const onClick = () => {
                                return handleClick(action);
                            };

                            if (action?.externalURL) {
                                return (
                                    <Link
                                        id={index}
                                        key={index}
                                        className="bg-transparent !no-underline hover:text-inherit"
                                        to={`${action?.externalURL}`}
                                        target="_blank"
                                        onClick={onClick}>
                                        <MenuItem icon={<ExternalLinkIcon />}>
                                            <Text>{action.name}</Text>
                                        </MenuItem>
                                    </Link>
                                );
                            }
                            return (
                                <MenuItem
                                    key={index}
                                    icon={<RepeatIcon />}
                                    onClick={onClick}>
                                    <Text>{action.name}</Text>
                                </MenuItem>
                            );
                        })}
                    {/* Delete a service  */}
                    {state?.allowDelete && (
                        <MenuItem
                            onClick={() => deleteService()}
                            icon={<TbTrash />}>
                            <Text marginBottom="0">Delete Service</Text>
                        </MenuItem>
                    )}
                </DashboardCardEditButton>
            </CardBody>
        </Card>
    );
};

export default VendorCard;
